// === Global API fetch interceptor ===
// One chokepoint for every call to the Click2Gro API, so session handling is
// resilient and consistent (the web app's apiFetch does the equivalent).
//
// For requests to the API base it:
//   1. Injects a FRESH access token from the current Supabase session, replacing
//      whatever (possibly stale) token a page attached. This kills the entire
//      class of "expired token in a captured closure → 401" bugs.
//   2. On 401, refreshes the session once and retries.
//   3. If it's still 401/403, the session is genuinely dead → clear it, sign out
//      and redirect to /login, so an expired session self-heals to the login page
//      instead of leaving a wall of 401s on a broken page.
//
// Calls to anything other than the API base (Supabase auth at *.supabase.co,
// same-origin /config.json, assets, …) pass straight through untouched.

import { getSupabaseClient } from './auth';

const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';

let signedOutLatch = false;

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

export function installApiInterceptor(): void {
  // Idempotent — guard against double-install (HMR / StrictMode).
  const w = window as unknown as { __c2gApiInterceptor?: boolean };
  if (w.__c2gApiInterceptor) return;
  w.__c2gApiInterceptor = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Only touch calls to our API; everything else is untouched.
    if (!urlOf(input).startsWith(API_BASE)) {
      return originalFetch(input, init);
    }

    const supabase = await getSupabaseClient();

    const withAuth = (token: string | undefined): RequestInit => {
      const headers = new Headers(
        init?.headers ?? (input instanceof Request ? input.headers : undefined),
      );
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return { ...init, headers };
    };

    // 1. Always use a fresh token from the live session.
    let token = (await supabase.auth.getSession()).data.session?.access_token;
    let res = await originalFetch(input, withAuth(token));

    // 2. Stale token → refresh once and retry.
    if (res.status === 401) {
      const refreshed = (await supabase.auth.refreshSession()).data.session?.access_token;
      if (refreshed && refreshed !== token) {
        token = refreshed;
        res = await originalFetch(input, withAuth(token));
      }
    }

    // 3. Still unauthorized → session is dead. Sign out + redirect to login.
    if ((res.status === 401 || res.status === 403) && !signedOutLatch) {
      signedOutLatch = true;
      // Clear the persisted session synchronously first, so the login page
      // doesn't see a stale `user` and bounce back into the app.
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
            localStorage.removeItem(key);
          }
        }
      } catch { /* ignore */ }
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      if (!window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }

    return res;
  };
}
